import styled from "styled-components";
import {fileKindPalette, type FileKindTone} from "../../download/fileKind";
import {brand, fontFamilies, radii} from "theme/tokens";
import {outlinedControl} from "./outlinedControl.styles";

/**
 * Externalized styles for FileSummary: the borderless payload box, pinned usage header, scrolling
 * file rows, and collapse / "+ add more" controls. Colors use tokens or the composer's accent variables.
 *
 * **The card's scroll model lives here.** The card (`CreateFlow`'s `Card` → `TransferPanel`'s `Root`)
 * is a capped-height flex column; everything else in it is `flex: none`, so this box is the only child
 * that may shrink and {@link ListScroll} is the card's ONE scroller. `min-height: 0` on every link of
 * the chain is load-bearing — without it a flex item refuses to shrink below its content and the card
 * overflows instead of the list scrolling. Where the card is NOT capped (a window under 520px tall, and a
 * phone while a selection is composed, kitecorp/transferify-ui#3410; see `CreateFlow.styles.Card`) nothing
 * shrinks: the list is as tall as its rows and the page scrolls instead.
 */

/**
 * The borderless file-summary container. The usage header pins to its top, the file list absorbs
 * the leftover height, and "+ add more" pins to its bottom. The sender accent is supplied once by
 * TransferPanel; without personalization this uses the recipient panel's faint product-blue wash.
 */
export const Summary = styled.div`
	border: 0;
	border-radius: ${radii.panel};
	background: var(--sender-accent-tint, color-mix(in srgb, ${({theme}) => theme.tokens.accentTint} 45%, ${({theme}) => theme.tokens.card}));
	display: flex;
	flex-direction: column;
	/* The card's only shrinkable child — everything below it (email, mode row, Options, Send) is
	   flex:none, so all the shrinking lands here and resolves inside ListScroll. */
	flex: 1 1 auto;
	min-height: 0;
	padding: 9px;
`;

/**
 * The pinned payload header slot (ui#216): "PAYLOAD / N FILES / size / free / UPGRADE" sits at the TOP
 * of the card and never scrolls away — the file rows travel underneath it.
 */
export const UsageHeader = styled.div`
	flex: none;
	padding-bottom: 2px;
`;

/**
 * The file rows — the card's ONLY scroll region (ui#216). It takes whatever height the pinned header,
 * the add-more row and the rest of the card leave over; `min-height: 0` lets it shrink below its
 * content so the overflow becomes a scrollbar here instead of blowing out the card.
 */
export const ListScroll = styled.div`
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	/* overflow-y:auto computes overflow-x:auto (spec); the rows already ellipsize, never scroll sideways. */
	overflow-x: hidden;
`;

/** A {@link FileRow}'s height before a windowed list measures it: the 42px thumb plus 7px padding above and below. */
export const FILE_ROW_HEIGHT_ESTIMATE_PX = 42 + 7 * 2;

export const FileRow = styled.div`
	display: flex;
	align-items: center;
	gap: 11px;
	padding: 7px 6px;
	border-radius: 11px;
`;

export const Thumb = styled.div<{$tone: FileKindTone}>`
	width: 42px;
	height: 42px;
	border-radius: 10px;
	flex: none;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 700;
	font-size: 10px;
	color: ${({theme, $tone}) => fileKindPalette(theme.tokens, $tone).ink};
	letter-spacing: 0.02em;
	background: ${({theme, $tone}) => fileKindPalette(theme.tokens, $tone).surface};
`;

export const FileInfo = styled.div`
	flex: 1;
	min-width: 0;
`;

export const FileName = styled.div`
	font-weight: 600;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const FileSize = styled.div`
	font-size: 12px;
	color: ${({theme}) => theme.tokens.ink3};
	margin-top: 1px;
`;

export const RemoveButton = styled.button`
	width: 26px;
	height: 26px;
	border-radius: 8px;
	border: none;
	background: transparent;
	color: ${({theme}) => theme.tokens.ink3};
	cursor: pointer;
	font-size: 13px;
	flex: none;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: 0.14s;

	&:hover {
		background: ${({theme}) => theme.tokens.field};
		color: ${brand.error};
	}
`;

/** The collapsed "N files · total" row, and the expanded state's "show less" row. Never scrolls. */
export const CollapsedRow = styled.button`
	display: flex;
	align-items: center;
	gap: 11px;
	padding: 9px 8px;
	border-radius: 11px;
	cursor: pointer;
	width: 100%;
	flex: none;
	border: none;
	background: none;
	font-family: inherit;
	text-align: left;
	transition: background 0.14s;

	&:hover {
		background: ${({theme}) => theme.tokens.field};
	}
`;

export const CollapsedIcon = styled.div`
	width: 42px;
	height: 42px;
	border-radius: 10px;
	background: linear-gradient(135deg, ${({theme}) => theme.tokens.blue}, ${({theme}) => theme.tokens.teal});
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
`;

export const CollapsedMeta = styled.div`
	flex: 1;
	min-width: 0;
`;

export const CollapsedTitle = styled.div`
	font-weight: 700;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink};
`;

export const CollapsedSub = styled.div`
	font-size: 12px;
	color: ${({theme}) => theme.tokens.ink3};
`;

export const CollapseArrow = styled.span`
	color: ${({theme}) => theme.tokens.ink3};
	font-size: 12px;
`;

/**
 * The pinned add row: "+ add more" beside "+ add a folder" (ui#1314).
 *
 * The two buttons SHARE one line at every width and never wrap onto a second one. That is a height
 * budget, not a taste: at 375x812 the whole card has to stay inside
 * {@link ../landing/campaignSpace.STACKED_CARD_HEIGHT_BUDGET_PX}, or the landing's campaign-fit
 * measurement stops finding room below it and moves the promotion to a fixed strip ABOVE the card —
 * the opposite of the create-panel-leads order ui#1106 settled. A wrapped second button row costs
 * 45px of that budget, more than any language had spare, so the labels share the width instead.
 *
 * The per-language slack lives with the budget, not here: two copies of it drifted out of date once
 * already (ui#2475), and one of them is what hid German being 15px from the flip.
 */
export const AddRow = styled.div`
	display: flex;
	gap: 6px;
	flex: none;
	margin-top: 1px;
`;

/**
 * The drag hint under the add row, and the width where it stands down.
 *
 * A block so DragOnly can wrap it (see FileSummary) and remove the line entirely where no pointer
 * can drag. It also stands down on any narrow viewport, because at 375px the card cannot afford all
 * three of: two add controls, their row, and a hint line. A hint line costs 20px of
 * {@link ../landing/campaignSpace.STACKED_CARD_HEIGHT_BUDGET_PX}, which is more than the narrowest
 * language has spare — so keeping it here would push that landing into the wrong order for a
 * sentence about a gesture. The empty drop area still teaches the gesture in its own sub-line, and
 * the whole-page drop overlay still answers a drag wherever one is possible.
 */
export const AddHint = styled.span`
	display: block;
	flex: none;
	margin-top: 4px;
	text-align: center;
	color: ${({theme}) => theme.tokens.ink2};
	font: 500 12px ${fontFamilies.sans};

	@media (max-width: 420px) {
		display: none;
	}
`;

/**
 * One add affordance. Both share this element, so the pair cannot drift apart visually, and the
 * frame is the shared {@link outlinedControl} the empty card's picker pair also wears (owner
 * 2026-09-15, ui#1314) — the same choice at two moments should not be two different controls.
 */
export const AddMore = styled.button`
	${outlinedControl}
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	/* Equal halves of the row. A zero basis plus min-width 0 is what keeps them on ONE line: with a
	   content-sized minimum the pair is about 3px too wide for a 375px card and drops to two rows,
	   which is the 45px this row must not spend (see AddRow). No backticks in this comment: it sits
	   inside a template literal, where one would end the CSS mid-rule. */
	flex: 1 1 0;
	min-width: 0;
	/* Tighter than the hero pair, which is a 20px radius: this row sits inside the payload box and
	   borrows the file rows' corner, not the card's. */
	border-radius: 11px;
	padding: 10px;
	font: 600 12.5px ${fontFamilies.sans};
`;
